import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { useCaptureUpload } from "./useCaptureUpload";
import { useTrash } from "./useTrash";
import { ALL_CAPTURES_FILTER, TODAY_FILTER } from "../lib/captureFilters";
import { listCaptures, createCapture, deleteCapture } from "../lib/offlineCaptures";

export function useDashboardData() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [folders, setFolders] = useState([]);
  const [captures, setCaptures] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewMode, setViewMode] = useState("captures"); // 'captures' | 'trash'
  const [editCapture, setEditCapture] = useState(null);
  const [tagCapture, setTagCapture] = useState(null);
  const [newTagOpen, setNewTagOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [singleSendCapture, setSingleSendCapture] = useState(null);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [shareLinkOpen, setShareLinkOpen] = useState(false);
  const [shareLinkUrl, setShareLinkUrl] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // "desc" = newest first, "asc" = oldest first
  const [sortField, setSortField] = useState("updated_at"); // "updated_at" | "created_at"
  const latestCapturesRequestRef = useRef(0);
  // Desktop-only tabbed split-view state (max 2 open notes side-by-side).
  // Mobile keeps using editCapture/setEditCapture above via the modal.
  const [openTabs, setOpenTabs] = useState([]);
  const [focusedTabId, setFocusedTabId] = useState(null);
  // At most 1 tab can be pinned at a time - pinning a note keeps its pane
  // fixed while the other pane keeps getting replaced by newly opened notes.
  const [pinnedTabId, setPinnedTabId] = useState(null);

  const loadTags = useCallback(async () => {
    const { data } = await api.get("/tags");
    setTags(data);
  }, []);

  const loadFolders = useCallback(async () => {
    const { data } = await api.get("/folders");
    setFolders(data);
  }, []);

  const loadCaptures = useCallback(async (tagId) => {
    // Guard against out-of-order responses: if the user switches tags quickly,
    // an earlier request can resolve after a newer one and overwrite the
    // correct list with stale data from the previously selected tag.
    const requestId = ++latestCapturesRequestRef.current;
    const params = { sort: sortOrder, sort_by: sortField };
    if (tagId === ALL_CAPTURES_FILTER) params.show_all = true;
    else if (tagId === TODAY_FILTER) {
      params.tag_id = TODAY_FILTER;
      // Midnight in the browser's local timezone, sent as its UTC-instant
      // equivalent. Without this, the server would compute "today" from
      // its own UTC clock, which is wrong for anyone outside UTC: e.g. in
      // China (UTC+8) a note edited at 7am local time is still "yesterday"
      // by UTC's calendar, so it would silently disappear from "今日" until
      // UTC catches up 8 hours later - exactly the bug the user reported.
      const localMidnight = new Date();
      localMidnight.setHours(0, 0, 0, 0);
      params.today_start = localMidnight.toISOString();
    }
    else if (tagId) params.tag_id = tagId;
    const data = await listCaptures(params);
    if (requestId === latestCapturesRequestRef.current) {
      setCaptures(data);
    }
  }, [sortOrder, sortField]);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  useEffect(() => {
    loadTags();
    loadFolders();
  }, [loadTags, loadFolders]);

  useEffect(() => {
    loadCaptures(selectedTagId);
    setSelectedIds([]);
  }, [selectedTagId, loadCaptures]);

  useEffect(() => {
    const handler = () => {
      loadCaptures(selectedTagId);
      loadTags();
    };
    window.addEventListener("capture-imported", handler);
    return () => window.removeEventListener("capture-imported", handler);
  }, [selectedTagId, loadCaptures, loadTags]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleDeleteCapture = async (id) => {
    await deleteCapture(id);
    setCaptures((prev) => prev.filter((c) => c.id !== id));
    setSelectedIds((prev) => prev.filter((x) => x !== id));
    setEditCapture(null);
    // Deleting the note itself always removes its tab, even if pinned.
    setOpenTabs((prev) => prev.filter((tb) => tb.id !== id));
    setFocusedTabId((current) => (current === id ? null : current));
    setPinnedTabId((prev) => (prev === id ? null : prev));
    loadTags();
    toast.success(t("dashboard.movedToTrashSuccess"));
  };

  const handleBulkDelete = async () => {
    await api.post("/captures/bulk-delete", { capture_ids: selectedIds });
    setCaptures((prev) => prev.filter((c) => !selectedIds.includes(c.id)));
    setOpenTabs((prev) => prev.filter((t) => !selectedIds.includes(t.id)));
    setPinnedTabId((prev) => (selectedIds.includes(prev) ? null : prev));
    setSelectedIds([]);
    loadTags();
    toast.success(t("dashboard.bulkMovedToTrashSuccess"));
  };

  // Opens a note as a tab in the desktop split-view. If it's already open,
  // just brings focus to it. Otherwise appends it (up to 2 tabs); once 2
  // tabs are already open, the pinned tab (if any) is always kept and the
  // other one gets replaced; with no pin, the least-recently-viewed one is
  // replaced instead.
  const openNoteTab = (capture) => {
    setFocusedTabId(capture.id);
    setOpenTabs((prev) => {
      const existingIndex = prev.findIndex((tb) => tb.id === capture.id);
      if (existingIndex !== -1) {
        const next = [...prev];
        next[existingIndex] = { ...capture, lastViewedAt: Date.now() };
        return next;
      }
      if (prev.length < 2) {
        return [...prev, { ...capture, lastViewedAt: Date.now() }];
      }
      const pinnedIndex = prev.findIndex((tb) => tb.id === pinnedTabId);
      const evictIndex = pinnedIndex !== -1
        ? (pinnedIndex === 0 ? 1 : 0)
        : (prev[0].lastViewedAt <= prev[1].lastViewedAt ? 0 : 1);
      const next = [...prev];
      next[evictIndex] = { ...capture, lastViewedAt: Date.now() };
      return next;
    });
  };

  const focusNoteTab = (id) => {
    setFocusedTabId(id);
    setOpenTabs((prev) => prev.map((tb) => (tb.id === id ? { ...tb, lastViewedAt: Date.now() } : tb)));
  };

  // Pinning is exclusive (max 1 pinned tab) - pinning a tab automatically
  // un-pins whichever other tab was previously pinned.
  const togglePinTab = (id) => {
    setPinnedTabId((prev) => (prev === id ? null : id));
  };

  const closeNoteTab = (id) => {
    if (id === pinnedTabId) return;
    setOpenTabs((prev) => {
      const next = prev.filter((tb) => tb.id !== id);
      setFocusedTabId((current) => (current === id ? (next[0]?.id ?? null) : current));
      return next;
    });
  };

  const swapTabs = () => {
    setOpenTabs((prev) => (prev.length === 2 ? [prev[1], prev[0]] : prev));
  };

  const handleDeleteTag = async (id) => {
    await api.delete(`/tags/${id}`);
    setTags((prev) => prev.filter((t) => t.id !== id));
    if (selectedTagId === id) setSelectedTagId(null);
    loadCaptures(selectedTagId === id ? null : selectedTagId);
    toast.success(t("sidebar.tagDeletedSuccess"));
  };

  const handleRenameTag = async (id, name) => {
    const { data } = await api.patch(`/tags/${id}`, { name });
    setTags((prev) => prev.map((tag) => (tag.id === id ? data : tag)));
    toast.success(t("sidebar.tagRenamedSuccess"));
  };

  const handleMoveTagToFolder = async (tagId, folderId) => {
    try {
      const { data } = await api.patch(`/tags/${tagId}/move`, { folder_id: folderId });
      setTags((prev) => prev.map((tag) => (tag.id === tagId ? data : tag)));
    } catch (e) {
      toast.error(t("sidebar.moveTagFailedError"));
    }
  };

  const handleDeleteFolder = async (id) => {
    await api.delete(`/folders/${id}`);
    setFolders((prev) => prev.filter((f) => f.id !== id));
    loadTags();
    toast.success(t("sidebar.folderDeletedSuccess"));
  };

  const handleRenameFolder = async (id, name) => {
    const { data } = await api.patch(`/folders/${id}`, { name });
    setFolders((prev) => prev.map((folder) => (folder.id === id ? data : folder)));
    toast.success(t("sidebar.folderRenamedSuccess"));
  };

  const handleUpdateFolderColor = async (id, color) => {
    const { data } = await api.patch(`/folders/${id}`, { color });
    setFolders((prev) => prev.map((folder) => (folder.id === id ? data : folder)));
    toast.success(t("sidebar.folderColorUpdatedSuccess"));
  };

  const handleBulkMoveTagsToFolder = async (tagIds, folderId) => {
    try {
      const results = await Promise.all(tagIds.map((tagId) => api.patch(`/tags/${tagId}/move`, { folder_id: folderId })));
      const updatedById = new Map(results.map((res) => [res.data.id, res.data]));
      setTags((prev) => prev.map((tag) => updatedById.get(tag.id) || tag));
      toast.success(t("sidebar.bulkMoveSuccess", { count: tagIds.length }));
    } catch (e) {
      toast.error(t("sidebar.bulkMoveFailedError"));
    }
  };

  const selectTag = (tagId) => {
    setViewMode("captures");
    setSelectedTagId(tagId);
  };

  const handleCaptureSaved = (updated) => {
    setCaptures((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setOpenTabs((prev) => prev.map((tb) => (tb.id === updated.id ? { ...updated, lastViewedAt: tb.lastViewedAt } : tb)));
  };

  const handleCaptureTagged = (updated) => {
    setCaptures((prev) => {
      // If a specific tag filter (or the untagged view) is active and this
      // update means the capture no longer belongs in it, drop it from the
      // visible list immediately instead of leaving a stale patched-in-place
      // item until the next full reload.
      if (selectedTagId && selectedTagId !== ALL_CAPTURES_FILTER) {
        if (!updated.tags?.includes(selectedTagId)) {
          return prev.filter((c) => c.id !== updated.id);
        }
      } else if (!selectedTagId) {
        if (updated.tags && updated.tags.length > 0) {
          return prev.filter((c) => c.id !== updated.id);
        }
      }
      return prev.map((c) => (c.id === updated.id ? updated : c));
    });
    setOpenTabs((prev) => prev.map((tb) => (tb.id === updated.id ? { ...updated, lastViewedAt: tb.lastViewedAt } : tb)));
    loadTags();
  };

  const handleToggleFavorite = async (capture) => {
    const favoritesTag = tags.find((tg) => tg.is_favorites);
    if (!favoritesTag) return;
    const isFavorited = capture.tags?.includes(favoritesTag.id);
    const newTags = isFavorited
      ? capture.tags.filter((id) => id !== favoritesTag.id)
      : [...(capture.tags || []), favoritesTag.id];
    try {
      const { data } = await api.patch(`/captures/${capture.id}`, { tags: newTags });
      handleCaptureTagged(data);
    } catch (e) {
      toast.error(t("captureCard.toggleFavoriteFailedError"));
    }
  };

  const handleBulkTagged = (updatedList) => {
    setCaptures((prev) => prev.map((c) => updatedList.find((u) => u.id === c.id) || c));
    loadTags();
  };

  const handleGenerateLink = async (captureIds) => {
    try {
      const { data } = await api.post("/shares", { capture_ids: captureIds });
      // Points at the backend-rendered preview page (not the SPA route
      // directly) so chat-app link unfurling bots (DingTalk/WeChat Work etc,
      // which read raw HTML without running JS) see a per-share summary of
      // every capture instead of the app's generic static description. Real
      // users get redirected instantly to the actual share page.
      setShareLinkUrl(`${API}/shares/${data.token}/preview`);
      setShareLinkOpen(true);
    } catch (e) {
      toast.error(t("sidebar.generateLinkFailedError"));
    }
  };

  const handleGenerateTagLink = async (tagId) => {
    try {
      const { data } = await api.post(`/tags/${tagId}/share`);
      setShareLinkUrl(`${API}/shares/${data.token}/preview`);
      setShareLinkOpen(true);
    } catch (e) {
      if (e.response?.status === 400) {
        toast.error(t("sidebar.tagNoScreenshotsError"));
      } else {
        toast.error(t("sidebar.generateLinkFailedError"));
      }
    }
  };

  const handleCreateTextNote = async () => {
    try {
      const data = await createCapture({
        title: "",
        text_content: " ",
        tags: selectedTagId ? [selectedTagId] : [],
        title_language: i18n.language,
      });
      setCaptures((prev) => [data, ...prev]);
      // Desktop Dashboard no longer renders a Dialog for editCapture (it's
      // been replaced by the tabbed split-view), so open the new note as a
      // tab there; MobileDashboard still relies on editCapture/setEditCapture.
      setEditCapture(data);
      openNoteTab(data);
      loadTags();
    } catch (e) {
      toast.error(t("dashboard.newTextNoteFailedError"));
    }
  };

  const handleMergeCaptures = async (layout) => {
    try {
      // Backend returns one merged capture per batch of up to 8 selected
      // screenshots (2 columns x 4 rows), so a large selection can come back
      // as multiple new captures.
      const { data } = await api.post("/captures/merge", {
        capture_ids: selectedIds,
        layout,
        title_language: i18n.language,
      });
      const mergedList = Array.isArray(data) ? data : [data];
      setCaptures((prev) => [...mergedList, ...prev]);
      setSelectedIds([]);
      setEditCapture(mergedList[0]);
      openNoteTab(mergedList[0]);
      loadTags();
      toast.success(t("dashboard.mergeSuccess", { count: mergedList.length }));
    } catch (e) {
      toast.error(t("dashboard.mergeFailedError"));
    }
  };

  const upload = useCaptureUpload({ selectedTagId, loadCaptures, loadTags });
  const trash = useTrash({ loadTags, loadCaptures, selectedTagId });

  const openAnnotationEditor = (captureId) => {
    navigate(`/capture/${captureId}`);
  };

  const openTrash = () => {
    setViewMode("trash");
    trash.loadTrash();
  };

  const closeTrash = () => {
    setViewMode("captures");
  };

  const selectedCaptures = captures.filter((c) => selectedIds.includes(c.id));
  const emailTargets = singleSendCapture ? [singleSendCapture] : selectedCaptures;

  return {
    tags, setTags, folders, setFolders, captures, selectedTagId, setSelectedTagId, selectTag, selectedIds, setSelectedIds,
    viewMode, openTrash, closeTrash, openAnnotationEditor,
    editCapture, setEditCapture, tagCapture, setTagCapture,
    openTabs, focusedTabId, openNoteTab, focusNoteTab, closeNoteTab, swapTabs,
    pinnedTabId, togglePinTab,
    newTagOpen, setNewTagOpen, newFolderOpen, setNewFolderOpen, sendEmailOpen, setSendEmailOpen,
    singleSendCapture, setSingleSendCapture, bulkTagOpen, setBulkTagOpen,
    shareLinkOpen, setShareLinkOpen, shareLinkUrl,
    sortOrder, toggleSortOrder, sortField, setSortField,
    toggleSelect, handleDeleteCapture, handleBulkDelete, handleDeleteTag, handleRenameTag,
    handleMoveTagToFolder, handleDeleteFolder, handleRenameFolder, handleUpdateFolderColor, handleBulkMoveTagsToFolder,
    handleCaptureSaved, handleCaptureTagged, handleBulkTagged, handleGenerateLink, handleGenerateTagLink,
    handleCreateTextNote, handleMergeCaptures, handleToggleFavorite,
    selectedCaptures, emailTargets,
    ...upload,
    ...trash,
  };
}
